import { debounce } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';

const SECTION_ID = 'predictive-search-inline';

class InlineSearchComponent extends HTMLElement {
  #requestCounter = 0;
  #lastQuery = null;
  #lastPanelChildren = null;
  #abortOutside = null;

  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.panel = this.querySelector('.inline-search__panel');
    if (!this.input || !this.panel) return;

    if (this._connected) return;
    this._connected = true;
    this.input.addEventListener('input', this.#onInput);
    this.input.addEventListener('focus', this.#onFocus);
    this.input.addEventListener('keydown', this.#onKeyDown);
  }

  disconnectedCallback() {
    this._connected = false;
    this.#requestCounter++;
    this.input?.removeEventListener('input', this.#onInput);
    this.input?.removeEventListener('focus', this.#onFocus);
    this.input?.removeEventListener('keydown', this.#onKeyDown);
    this.#teardownOutsideListener();
  }

  #onInput = debounce((event) => {
    if (!this.isConnected) return;
    const q = this.input.value.trim();
    if (!q) {
      this.#close();
      return;
    }
    this.#fetchResults(q);
  }, 200);

  #onFocus = () => {
    const q = this.input.value.trim();
    if (!q) return;
    if (q === this.#lastQuery && this.#lastPanelChildren) {
      this.#open();
    } else {
      this.#fetchResults(q);
    }
  };

  async #fetchResults(q) {
    const requestId = ++this.#requestCounter;
    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', q);
    url.searchParams.set('resources[type]', 'product,query');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('resources[limit_scope]', 'each');

    this.panel.setAttribute('aria-busy', 'true');
    try {
      const html = await sectionRenderer.getSectionHTML(SECTION_ID, false, url);
      if (requestId !== this.#requestCounter) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const content = doc.querySelector('[data-inline-search-panel]');
      if (!content) {
        this.#close();
        return;
      }
      this.panel.replaceChildren(...content.children);
      this.#lastQuery = q;
      this.#lastPanelChildren = true;
      this.#selectedIndex = -1;
      this.#open();
    } catch (e) {
      if (requestId === this.#requestCounter) this.#close();
    } finally {
      this.panel.removeAttribute('aria-busy');
    }
  }

  #open() {
    this.panel.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
    this.#setupOutsideListener();
  }

  #close() {
    this.#requestCounter++;
    this.panel.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.#selectedIndex = -1;
    this.#teardownOutsideListener();
  }

  #setupOutsideListener() {
    if (this.#abortOutside) return;
    this.#abortOutside = new AbortController();
    document.addEventListener(
      'pointerdown',
      (e) => {
        if (!this.contains(e.target)) this.#close();
      },
      { signal: this.#abortOutside.signal }
    );
  }

  #teardownOutsideListener() {
    this.#abortOutside?.abort();
    this.#abortOutside = null;
  }

  #selectedIndex = -1;

  get #options() {
    return [...this.panel.querySelectorAll('[data-inline-option]')];
  }

  #onKeyDown = (event) => {
    const open = !this.panel.hidden;

    if (event.key === 'Escape') {
      if (open) {
        this.#close();
      } else if (this.input.value) {
        this.input.value = '';
      }
      return;
    }
    if (event.key === 'Tab') {
      this.#close();
      return;
    }

    if (!open) return;
    const options = this.#options;
    if (!options.length) return;

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const delta = event.key === 'ArrowDown' ? 1 : -1;
      this.#selectedIndex = (this.#selectedIndex + delta + options.length) % options.length;
      options.forEach((el, i) => {
        el.setAttribute('aria-selected', i === this.#selectedIndex ? 'true' : 'false');
        if (i === this.#selectedIndex) el.scrollIntoView({ block: 'nearest' });
      });
    } else if (event.key === 'Enter' && this.#selectedIndex >= 0) {
      event.preventDefault();
      const selected = options[this.#selectedIndex];
      if (selected instanceof HTMLAnchorElement) window.location.href = selected.href;
    }
  };
}

if (!customElements.get('inline-search-component')) {
  customElements.define('inline-search-component', InlineSearchComponent);
}
